import { SoftDeletable } from './entity';

export enum StepTask {
  TEXT = 'text',
  OCR = 'ocr',
  CLASSIFICATION = 'classification',
  ROIDETECTION = 'roidetection',
  SEGMENTATION = 'segmentation',
  POSITION = 'position',
  ROITRACKING = 'roitracking',
  MAPPING = 'mapping',
}

export interface PipelineStepParams {
  options: {
    [labelUuid: string]: { thrdisp?: number };
  };
  categorization: {
    category: string;
    options: string[];
  }[];
  multiple?: boolean;
  allowNewOptions?: boolean;
  enableMosaicView?: boolean;
  counter?: boolean;
}

export enum StepAction {
  USER_EXIT = 'user:exit',
  USER_CAPTURE = 'user:capture',
  ASSET_CREATED = 'asset:created',
  SAMPLE_CREATED = 'sample:created',
  ACQUISITION_FRAME = 'acquisition:frame',
  ACQUISITION_EXIT = 'acquisition:exit',
}

/**
 * The value stored in `PipelineStep.generateFinding`.
 *
 * Most steps emit a finding tied to one of the {@link StepAction} events
 * (e.g. `asset:created`, `sample:created`). Some steps — typically the
 * cloud-only ones surfaced as "no automatic finding" in the editor —
 * explicitly opt out by persisting the literal `'never'`.
 */
export type GenerateFindingValue = StepAction | 'never';

export enum ExecutorEntity {
  ALGORITHM = 'algorithm',
  USER = 'user',
}

export interface PipelineStepParameters extends SoftDeletable {
  id?: string;
  task: StepTask;
  executorEntity: ExecutorEntity;
  executorId: string;
  order: number;
  trigger: StepAction;
  generateFinding: GenerateFindingValue;
  params: any;
  pipelineId: string;
}

export class PipelineStep implements SoftDeletable {
  constructor(private attributes: PipelineStepParameters) {}

  public get deletedAt(): Date {
    return this.attributes.deletedAt;
  }

  public get id(): string {
    return this.attributes.id;
  }

  public get task(): StepTask {
    return this.attributes.task;
  }

  public get executorEntity(): ExecutorEntity {
    return this.attributes.executorEntity;
  }

  public get executorId(): string {
    return this.attributes.executorId;
  }

  public changeExecutor(algorithmId: string | undefined): void {
    const hasAlgorithm = !!algorithmId;
    this.attributes.executorEntity = hasAlgorithm ? ExecutorEntity.ALGORITHM : ExecutorEntity.USER;
    this.attributes.executorId = hasAlgorithm ? algorithmId : undefined;
  }

  public get outputLabels(): Set<string> {
    // Extract from categorization
    const categorization = this.attributes.params?.categorization ?? [];
    const categorizationLabels = Array.isArray(categorization)
      ? categorization.flatMap((category: { options: string[] }) => category.options ?? [])
      : [];

    // Extract from options
    const options = this.attributes.params?.options ?? {};
    const optionsLabels = Object.keys(options);

    return new Set([...categorizationLabels, ...optionsLabels]);
  }

  public get order(): number {
    return this.attributes.order;
  }

  public get trigger(): StepAction {
    return this.attributes.trigger;
  }

  public get generateFinding(): GenerateFindingValue {
    return this.attributes.generateFinding;
  }

  public get params(): any {
    return this.attributes.params;
  }

  public get pipelineId(): string {
    return this.attributes.pipelineId;
  }
}
